import React, { FC, useCallback } from "react";
import TourBooking from "./components/TourBooking";
import TourBookingField from "./components/TourBookingField";
import moment, { Moment } from "moment-timezone";
import "./App.css";
import PropertyService from "./service/property-service";
import { PropertyResource } from "./model/propertyResource";
import { GuestInfo } from "./model/guest-info";
import BookingService from "./service/booking-service";
import { GuestBookingData } from "./service/booking-service";
import { AuthBodyResponse } from "./service/types";
import { Grid, MuiThemeProvider, createMuiTheme } from "@material-ui/core";
const STRING = "string";
const STRING_ARRAY = "string[]";
const NUMBER = "number";
const DATE = "date";
const BOOLEAN = "bool";

const theme = createMuiTheme({
  typography: {
    fontFamily: ["Mallory", "Canela", "Roboto"].join(","),
    h1: {
      fontFamily: "Canela",
    },
    h2: {
      fontFamily: "Canela",
    },
    h3: {
      fontFamily: "Canela",
    },
    h4: {
      fontFamily: "Canela",
    },
    h5: {
      fontFamily: "Canela",
    },
    h6: {
      fontFamily: "Canela",
    },
    subtitle1: {
      fontFamily: "Mallory",
      fontWeight: 700,
      fontSize: "24px",
    },
    subtitle2: {
      fontFamily: "Mallory",
      fontWeight: 500,
      fontSize: "20px",
    },
    body1: {
      fontFamily: "Mallory",
      fontWeight: 500,
      fontSize: "14px",
      color: "#414752",
    },
    body2: {
      fontFamily: "Mallory",
      fontWeight: 400,
      fontSize: "14px",
      color: "#414752",
    },
    caption: {
      fontFamily: "Mallory",
      fontWeight: 500,
      fontSize: "16px",
    },
    button: {
      fontFamily: "Mallory",
      fontWeight: 700,
    },
  },
  palette: {
    primary: {
      light: "#757ce8",
      main: "#EF4E22",
      dark: "#002884",
      contrastText: "#fff",
    },
    secondary: {
      light: "#ff7961",
      main: "#f44336",
      dark: "#ba000d",
      contrastText: "#000",
    },
  },
});

const getUrlParam = (
  urlParams: URLSearchParams,
  key: string,
  defaultValue: string | string[] | number | boolean | Moment | undefined,
  paramType = STRING
): any => {
  let paramValue: any = urlParams.get(key);
  if (paramValue) {
    paramValue = paramValue.trim();
    if (paramType === NUMBER) {
      try {
        paramValue = parseInt(paramValue, 10);
      } catch (e) {
        paramValue = defaultValue;
      }
    } else if (paramType === DATE) {
      try {
        paramValue = moment(paramValue);
      } catch (dateError) {
        paramValue = defaultValue;
      }
    } else if (paramType === BOOLEAN) {
      const v = (paramValue ? paramValue + "" : "").toLowerCase() as string;
      if (v === "true" || v === "yes" || v === "1") {
        paramValue = true;
      }
    } else if (paramType === STRING_ARRAY) {
      // parse comma-delimited into array
      const array: string[] = paramValue.split(",");
      paramValue = array.map((s) => s.trim());
    }
  }
  if (paramValue === null || paramValue === undefined) {
    paramValue = defaultValue;
  }
  return paramValue;
};

interface AppProps {
  auth: AuthBodyResponse;
  allPropertyResources: PropertyResource[];
  allCities: PropertyResource[];
}

const App: FC<AppProps> = ({ auth, allPropertyResources, allCities }) => {
  moment.locale("en");
  moment().utc();
  const urlParams = new URLSearchParams(window.location.search);
  const [startDayOfWeek] = React.useState(moment().day());
  const [propertyId, setPropertyId] = React.useState<string | undefined>(getUrlParam(urlParams, "propertyId", undefined));
  const [realPageId, setRealPageId] = React.useState<string | undefined>(getUrlParam(urlParams, "realPageId", undefined));
  const [extraPropertyIds] = React.useState<string[]>(getUrlParam(urlParams, "extraPropertyIds", [], STRING_ARRAY));
  const [multiProp] = React.useState<boolean>(getUrlParam(urlParams, "multiProp", true, BOOLEAN));
  const [maxCalProps] = React.useState<number>(getUrlParam(urlParams, "maxCalProps", 5, NUMBER));
  const [radiusMiles] = React.useState<number>(getUrlParam(urlParams, "radiusMiles", 10, NUMBER));
  const [guestCardId] = React.useState<string>(getUrlParam(urlParams, "guestCardId", undefined));
  const [attributionId, setAttributionID] = React.useState<string>(getUrlParam(urlParams, "attributionId", ""));
  const [webClientId] = React.useState<string>(getUrlParam(urlParams, "webClientId", ""));
  const [tourStartDate] = React.useState<Moment>(getUrlParam(urlParams, "tourStartDate", moment(), DATE));
  const [numberOfWeeks] = React.useState<number>(getUrlParam(urlParams, "numberOfWeeks", 8, NUMBER));
  const [bookedById] = React.useState<string>(getUrlParam(urlParams, "bookedById", "", STRING));
  const [callTrxId] = React.useState<string>(getUrlParam(urlParams, "callTrxId", "", STRING));
  const [bookingRefName] = React.useState<string>(getUrlParam(urlParams, "bookingRefName", "", STRING));
  const [tourAction] = React.useState<string>(getUrlParam(urlParams, "action", "", STRING));
  const [advBookHours] = React.useState<number>(getUrlParam(urlParams, "advBookHours", 2, NUMBER));
  const [onSiteTours] = React.useState<boolean>(getUrlParam(urlParams, "onSiteTours", true, BOOLEAN));
  const [contactMeToSchedule] = React.useState<boolean>(getUrlParam(urlParams, "contactMeToSchedule", false, BOOLEAN));
  const [mode] = React.useState<string>(getUrlParam(urlParams, "mode", "web", STRING));
  const [referencePropertyResource, setReferencePropertyResource] = React.useState<PropertyResource>();
  const [guestBookingData, setGuestBookingData] = React.useState<GuestBookingData>();
  const [guestInfo, setGuestInfo] = React.useState<GuestInfo>({
    firstName: getUrlParam(urlParams, "FN", "", STRING),
    lastName: getUrlParam(urlParams, "LN", "", STRING),
    emailAddress: getUrlParam(urlParams, "EA", "", STRING),
    mobilePhone: getUrlParam(urlParams, "MN", "", STRING),
    smsOptIn: true,
    desiredMoveInDate: getUrlParam(urlParams, "MD", "", STRING),
    minBedrooms: getUrlParam(urlParams, "BD", "", STRING),
    minBathrooms: getUrlParam(urlParams, "BTH", "", STRING),
    tourType: getUrlParam(urlParams, "TT", "Tour", STRING),
    guestCardId: undefined,
    attributionId: getUrlParam(urlParams, "LS", "", STRING)
  });
  const [emailAddress] = React.useState<string[]>(getUrlParam(urlParams, "EA", "", STRING));
  const handleDoReset = useCallback(async () => {
    if (propertyId) {
      const propertyService = new PropertyService(tourStartDate, numberOfWeeks, advBookHours);
      propertyService.getProperty(propertyId, true, mode).then((propResource) => {
        setReferencePropertyResource(propResource);
      });
    }
  }, [advBookHours, mode, numberOfWeeks, propertyId, tourStartDate]);

  React.useEffect(() => {
      if (guestCardId && !emailAddress.length) {
      const bookingService = new BookingService();
      bookingService.fetchGuestInfo(guestCardId).then((g) => {  
        setGuestInfo(g);
        if (g.attributionId) {
          setAttributionID(g.attributionId);
        }
      });
    }
  }, [guestCardId]);

  React.useEffect(() => {
    if (bookingRefName) {
        const bookingService = new BookingService();
		const moveDate       = new Date().getDate();
		bookingService.guestBookingsByRef(bookingRefName).then((br) => {
           setGuestBookingData(br);
		   const guestInfo: GuestInfo = {
                firstName:         br?.Contact__r.FirstName,
                lastName:          br?.Contact__r.LastName,
                emailAddress:      br?.Contact__r.Email,
                mobilePhone:       br?.Contact__r.Phone,
				smsOptIn:          br? !br.Contact__r.HasOptedOutOfSMS__c : false,
				desiredMoveInDate: br?.Contact__r.pDesired_Move_In_Date__c,
				minBathrooms:      br?.Contact__r.Number_of_Baths__c,
				minBedrooms:       br?.Contact__r.Number_of_Beds__c
			};
		    setGuestInfo(guestInfo);
		});
    }
}, [bookingRefName]);

  React.useEffect(() => {
    if (realPageId) {
      if (allPropertyResources && realPageId && !propertyId) {
        const filtered = allPropertyResources.filter((r) => r.realPageId === realPageId);
        if (filtered && filtered.length > 0) {
          setPropertyId(filtered[0].propertyId);
          setRealPageId(undefined);
        }
      }
    }
  }, [allPropertyResources, propertyId, realPageId]);


  React.useEffect(() => {
    handleDoReset();
  }, [advBookHours, handleDoReset, mode, numberOfWeeks, propertyId, tourStartDate]);

  const handlePropertySelected = async (selectedProperty?: PropertyResource) => {
    if (selectedProperty) {
      setPropertyId(selectedProperty.propertyId);
      const propertyService = new PropertyService(tourStartDate, numberOfWeeks, advBookHours);
      await propertyService.populateTimeSlots([selectedProperty],mode);
    }
    setReferencePropertyResource(selectedProperty);
  };

  return (
      <MuiThemeProvider theme={theme}>
        <Grid style={{ width: "100%" }} container direction="column" justify="flex-start" alignItems="center" spacing={2}>
          {mode === 'cc' && referencePropertyResource && allPropertyResources && (
            <Grid item xs={12}>
              <TourBooking
                refPropertyResource={referencePropertyResource}
                extraPropertyIds={extraPropertyIds}
                multiProp={multiProp}
                maxCalProps={maxCalProps}
                guestInfoInput={guestInfo}
                radiusMiles={radiusMiles}
                tourStartDate={tourStartDate}
                advBookHours={advBookHours}
                callingMode={mode}
                numberOfWeeks={numberOfWeeks}
                bookedById={bookedById}
				bookingRefName={bookingRefName}
				tourAction={tourAction}
                callTrxId={callTrxId}
                startDayOfWeek={startDayOfWeek}
                numberDisplayDays={7}
                viewDate={moment(tourStartDate)}
                attributionId={attributionId}
                webClientId={webClientId}
                onSiteTours={onSiteTours}
                contactMeToSchedule={contactMeToSchedule}
                allPropertyResources={allPropertyResources}
                allCities={allCities}
                propertySelected={handlePropertySelected}
                doReset={handleDoReset}
              />
            </Grid>
          )}
		    {mode === 'field' && referencePropertyResource && allPropertyResources && (
            <Grid item xs={12}>
              <TourBookingField
                refPropertyResource={referencePropertyResource}
                extraPropertyIds={extraPropertyIds}
                multiProp={multiProp}
                maxCalProps={maxCalProps}
                guestInfoInput={guestInfo}
                radiusMiles={radiusMiles}
                tourStartDate={tourStartDate}
                advBookHours={advBookHours}
                callingMode={mode}
                numberOfWeeks={numberOfWeeks}
                bookedById={bookedById}
				bookingRefName={bookingRefName}
				tourAction={tourAction}
                callTrxId={callTrxId}
                startDayOfWeek={startDayOfWeek}
                numberDisplayDays={7}
                viewDate={moment(tourStartDate)}
                attributionId={attributionId}
                webClientId={webClientId}
                onSiteTours={onSiteTours}
                contactMeToSchedule={contactMeToSchedule}
                allPropertyResources={allPropertyResources}
                allCities={allCities}
                propertySelected={handlePropertySelected}
                doReset={handleDoReset}
              />
            </Grid>
          )}
        </Grid>
      </MuiThemeProvider>
  );
};

export default App;
